import { VoiceRecorder } from "capacitor-voice-recorder";
import {
	type ChangeEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../../lib/analytics";
import { api } from "../../../lib/api";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers, Question } from "../../../types/onboarding";
import styles from "./AdditionalInfoQuestion.module.css";

interface AdditionalInfoQuestionProps {
	question: Question;
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
}

const AUDIO_MIME_TYPE = "audio/aac";

// Matches a single occurrence of `example` as a whitespace-delimited token
// (bounded by start/end of string or whitespace). The capture groups preserve
// the surrounding boundaries so removal collapses to a single space without
// eating adjacent text. Escapes regex metacharacters in the condition phrase.
function buildExampleTokenRegex(example: string): RegExp {
	const escaped = example.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	return new RegExp(`(^|\\s)${escaped}(?=\\s|$)`);
}

export function AdditionalInfoQuestion({
	question,
	answers,
	onInputChange,
}: AdditionalInfoQuestionProps) {
	const { logOnboarding } = useAnalytics();
	const { t } = useClientTranslations("onboarding");
	const { t: tChat } = useClientTranslations("chat");
	const [inputText, setInputText] = useState<string>("");
	const [isRecording, setIsRecording] = useState(false);
	const [isTranscribing, setIsTranscribing] = useState(false);
	const hasInitialized = useRef(false);

	// Initialize from saved data on load
	useEffect(() => {
		if (!hasInitialized.current) {
			const savedText = answers[question.field];
			if (typeof savedText === "string" && savedText) {
				setInputText(savedText);
			}
			hasInitialized.current = true;
		}
	}, [answers, question.field]);

	// (Screen view is covered by the wizard's onboarding_screen_viewed event.)

	const handleInputChange = useCallback(
		(e: ChangeEvent<HTMLTextAreaElement>) => {
			const value = e.target.value;
			setInputText(value);
			onInputChange(question.field, value);
		},
		[question.field, onInputChange],
	);

	const handleTextareaFocus = () => {
		logOnboarding("additional_info_textarea_focused", {
			has_existing_text: inputText.length > 0,
		});
	};

	// A pill is selected iff its exact condition phrase appears in `inputText` as
	// a whitespace-delimited token. `inputText` is the single source of truth, so
	// prefilled answers and manual textarea edits stay consistent with the pills.
	const isExampleSelected = (example: string): boolean =>
		buildExampleTokenRegex(example).test(inputText);

	const handleExampleClick = (example: string) => {
		// Track engagement only — NOT which example. The example chips are health
		// conditions, so the specific value tied to an identified user is PHI.
		logOnboarding("additional_info_example_clicked");

		let newText: string;
		if (isExampleSelected(example)) {
			// Deselect: remove just this one token (match exact phrase once, not a
			// global blanket replace), then normalize only the affected spacing.
			newText = inputText
				.replace(buildExampleTokenRegex(example), " ")
				.replace(/\s{2,}/g, " ")
				.trim();
		} else {
			// Select: append the condition, preserving any existing text.
			newText = inputText ? `${inputText} ${example}` : example;
		}

		setInputText(newText);
		onInputChange(question.field, newText);
	};

	// Voice recording handler
	const handleMicClick = async () => {
		if (isRecording) {
			// Stop recording
			setIsRecording(false);
			setIsTranscribing(true);
			logOnboarding("additional_info_voice_stopped");

			try {
				// Stop recording and get the result
				const result = await VoiceRecorder.stopRecording();
				const recordDataBase64 = result.value?.recordDataBase64;

				if (!recordDataBase64) {
					throw new Error("No recording data received");
				}

				// Convert base64 to blob
				const base64Data = recordDataBase64;
				const byteCharacters = atob(base64Data);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const audioBlob = new Blob([byteArray], {
					type: result.value?.mimeType ?? AUDIO_MIME_TYPE,
				});

				// Validate audio blob
				if (audioBlob.size === 0) {
					throw new Error("Recording is empty. Please try again.");
				}

				if (audioBlob.size < 100) {
					throw new Error(
						"Recording is too short. Please speak for at least 1 second.",
					);
				}

				// Send to transcription API
				const formData = new FormData();
				// Use correct file extension based on mime type
				// iOS returns audio/aac, web returns audio/webm
				const fileExtension = audioBlob.type.includes("aac") ? "m4a" : "webm";
				const fileName = `audio.${fileExtension}`;
				formData.append("audio", audioBlob, fileName);

				const response = await api.post<{ text: string }>(
					"/api/chat/transcribe",
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				);

				if (response.data?.text) {
					const transcribedText = response.data.text;
					setInputText(transcribedText);
					onInputChange(question.field, transcribedText);

					logOnboarding("additional_info_voice_transcribed", {
						success: true,
						transcription_length: transcribedText.length,
					});
				}
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Transcription failed";
				toast.error(errorMessage);
				console.error("[Voice Recording] Transcription error:", err);
				logOnboarding("additional_info_voice_transcribed", {
					success: false,
					error: errorMessage,
				});
			} finally {
				setIsTranscribing(false);
			}
		} else {
			// Start recording
			try {
				// Check if device can record
				const canRecord = await VoiceRecorder.canDeviceVoiceRecord();

				if (!canRecord.value) {
					toast.error(tChat("toast.deviceNotSupported"));
					return;
				}

				// Request permission
				const hasPermission =
					await VoiceRecorder.requestAudioRecordingPermission();

				if (!hasPermission.value) {
					toast.error(
						"Microphone permission denied. Please enable it in settings.",
					);
					return;
				}

				setIsRecording(true);
				setInputText("");
				logOnboarding("additional_info_voice_started");

				// Start recording
				await VoiceRecorder.startRecording();
			} catch (err) {
				toast.error(tChat("toast.couldNotStartRecording"));
				setIsRecording(false);
				console.error("[Voice Recording] Start recording error:", err);
			}
		}
	};

	const exampleConditions = [
		t("additionalInfo.examples.bloodClotting"),
		t("additionalInfo.examples.anxiety"),
		t("additionalInfo.examples.pregnancyLoss"),
		t("additionalInfo.examples.endometriosis"),
	];

	return (
		<div className={styles.container}>
			<div className={styles.inputCard}>
				<div className={styles.questionSection}>
					<p className={styles.questionText}>{t("additionalInfo.question")}</p>
					<p className={styles.questionSubtext}>
						{t("additionalInfo.questionSubtext")}
					</p>
				</div>

				<div className={styles.inputWrapper}>
					<textarea
						value={inputText}
						onChange={handleInputChange}
						onFocus={handleTextareaFocus}
						placeholder={t("additionalInfo.placeholder")}
						className={styles.input}
						disabled={isRecording || isTranscribing}
						rows={3}
					/>
					<button
						type="button"
						onClick={handleMicClick}
						className={`${styles.micButton} ${isRecording ? styles.micButtonRecording : ""}`}
						disabled={isTranscribing}
						aria-label={
							isRecording ? "Stop recording" : "Start voice recording"
						}
					>
						{isTranscribing ? (
							<span className={styles.loadingDots}>...</span>
						) : (
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className={styles.micIcon}
							>
								<title>
									{isRecording
										? tChat("voiceRecording.stop")
										: tChat("voiceRecording.start")}
								</title>
								<path
									d="M12 15C13.66 15 15 13.66 15 12V6C15 4.34 13.66 3 12 3C10.34 3 9 4.34 9 6V12C9 13.66 10.34 15 12 15Z"
									fill="currentColor"
								/>
								<path
									d="M17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12H5C5 15.53 7.61 18.43 11 18.92V22H13V18.92C16.39 18.43 19 15.53 19 12H17Z"
									fill="currentColor"
								/>
							</svg>
						)}
					</button>
				</div>
			</div>

			<div className={styles.examplesSection}>
				<p className={styles.examplesTitle}>
					{t("additionalInfo.examplesTitle")}
				</p>
				<div className={styles.examplesGrid}>
					{exampleConditions.map((condition, index) => {
						const isSelected = isExampleSelected(condition);
						return (
							<button
								key={index}
								type="button"
								aria-pressed={isSelected}
								aria-label={condition}
								className={`${styles.examplePill}${isSelected ? ` ${styles.examplePillSelected}` : ""}`}
								onClick={() => handleExampleClick(condition)}
							>
								<span
									className={`${styles.exampleText}${isSelected ? ` ${styles.exampleTextSelected}` : ""}`}
								>
									{condition}
								</span>
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
}
